import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  DEFAULT_PRODUCT_IDENTIFIER,
  ProductIdentifierKey,
  coerceProductIdentifier,
} from '../models/product-identifier';

interface FeaturesResponse {
  installed_modules: string[];
  show_total_quantity?: boolean;
  allow_create_shipping_address?: boolean;
  primary_product_identifier?: string;
  traditional_contacts_enabled?: boolean;
  async_erp_sync?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  private installedModules$ = new BehaviorSubject<string[]>([]);
  private showTotalQuantity$ = new BehaviorSubject<boolean>(false);
  private allowCreateShippingAddress$ = new BehaviorSubject<boolean>(false);
  private primaryProductIdentifier$ = new BehaviorSubject<ProductIdentifierKey>(
    DEFAULT_PRODUCT_IDENTIFIER
  );
  private traditionalContactsEnabled$ = new BehaviorSubject<boolean>(false);
  private asyncErpSyncEnabled$ = new BehaviorSubject<boolean>(false);
  private loaded = false;

  constructor(private http: HttpClient) {}

  loadFeatures(force = false): Observable<string[]> {
    if (this.loaded && !force) {
      return this.installedModules$.pipe(take(1));
    }

    return this.http
      .get<{ data: any; object: FeaturesResponse }>(
        `${environment.BASE_URL_API}features`
      )
      .pipe(
        map((res) => res?.object ?? res?.data ?? {}),
        tap((features: FeaturesResponse) => {
          this.installedModules$.next(features.installed_modules ?? []);
          this.showTotalQuantity$.next(!!features.show_total_quantity);
          this.allowCreateShippingAddress$.next(
            !!features.allow_create_shipping_address
          );
          this.primaryProductIdentifier$.next(
            coerceProductIdentifier(features.primary_product_identifier)
          );
          this.traditionalContactsEnabled$.next(
            !!features.traditional_contacts_enabled
          );
          this.asyncErpSyncEnabled$.next(!!features.async_erp_sync);
          this.loaded = true;
        }),
        map((features: FeaturesResponse) => features.installed_modules ?? []),
        catchError(() => {
          this.loaded = true;
          return of([]);
        })
      );
  }

  isModuleInstalled(moduleName: string): boolean {
    return this.installedModules$.value.includes(moduleName);
  }

  isModuleInstalled$(moduleName: string): Observable<boolean> {
    return this.installedModules$.pipe(map((m) => m.includes(moduleName)));
  }

  // Pricebook/loyalty feature hidden front-end-wide due to business decision (WB-6694).
  // Not deleted — this getter is the single master switch that gates every pricebook
  // reference across the app. Restore by returning isModuleInstalled('wolsal_loyalty').
  get loyaltyEnabled(): boolean {
    return false;
  }

  // Pricebook/loyalty feature hidden front-end-wide due to business decision (WB-6694).
  // Not deleted — restore by returning isModuleInstalled$('wolsal_loyalty').
  get loyaltyEnabled$(): Observable<boolean> {
    return of(false);
  }

  // "Total Quantities" was removed app-wide (#1151) since summing lines across
  // different UoMs (pcs, mt, kg) is misleading. Some tenants (e.g. Landoll)
  // only ever order in a single UoM, so it's restored per-tenant. Source of
  // truth is the backend (GET /v1/features -> show_total_quantity), a plain
  // per-database Settings UI toggle — see
  // wolsal_addons/services/total_quantity_flag.py.
  get showTotalQuantity(): boolean {
    return this.showTotalQuantity$.value;
  }

  // the "Create new shipping address" button (shown when the AI fails
  // to match a shipping address) is gated per-tenant. Some tenants' ERPs reject
  // new addresses on order push, so the button must not render at all for them.
  // Disabled by default; enabled per-database from Odoo Settings > Feature Flags
  // (GET /v1/features -> allow_create_shipping_address).
  get allowCreateShippingAddress(): boolean {
    return this.allowCreateShippingAddress$.value;
  }

  // Which product identifier this tenant recognises its products by, used as the
  // single primary code rendered next to a product name (WB-7419). Joncoux sales
  // admins work only with their erp_id and don't recognise names/SKUs/EANs, so
  // showing the SKU there is pure noise. Per-database Settings value — see
  // wolsal_addons/services/product_identifier_config.py (GET /v1/features ->
  // primary_product_identifier). Defaults to 'sku' so every other tenant is
  // unaffected. Never read this to render a code directly: go through
  // ProductCodeService, which applies the mandatory SKU fallback.
  get primaryProductIdentifier(): ProductIdentifierKey {
    return this.primaryProductIdentifier$.value;
  }

  // Traditional (Odoo) Contacts are being retired behind a per-tenant flag (WB-7089/WB-7367):
  // the Email Registry becomes the sole contact surface when this is off. Tenant-level Settings
  // toggle, editable with no restart (GET /v1/features -> traditional_contacts_enabled) — see
  // wolsal_addons/services/traditional_contacts_flag.py. Defaults false on fetch failure, matching
  // the backend resolver's own fallback; existing tenants are backfilled to true server-side.
  get traditionalContactsEnabled(): boolean {
    return this.traditionalContactsEnabled$.value;
  }

  // WB-7957/WB-8276: async ERP sync. Confirming an order pushes it to Connect
  // and freezes it in "pending"/"processing" awaiting an async callback,
  // instead of assuming a synchronous ERP answer. The order-detail
  // order.erp_sync.sync_status badge and error banner are gated on this —
  // off (default), the order looks exactly as it does today. Per-database
  // Settings toggle, no restart required
  // (GET /v1/features -> async_erp_sync) — see
  // wolsal_addons/services/async_erp_sync_flag.py. Enabled for Equilibria
  // only until the full Quote & Order async rework (WB-6373) ships.
  get asyncErpSyncEnabled(): boolean {
    return this.asyncErpSyncEnabled$.value;
  }

  // Every pricing surface is gated on this: without the addon there is no
  // backend behind them (GET /v1/features -> installed_modules).
  get pricingEnabled(): boolean {
    return this.isModuleInstalled('wolsal_pricing');
  }

  get pricingEnabled$(): Observable<boolean> {
    return this.isModuleInstalled$('wolsal_pricing');
  }
}
